import { Task as TaskBase, TaskStep, TaskStepStatus, TaskStepType } from "cloud-core/tasks/Task";

export class Task extends TaskBase<number> {}

export interface TaskLogEntry {
    organisationId: string;
    taskId: string;
    timestamp: number;
    type: string;
    job?: {
        jobId: string;
        type: string;
    };
    extraData: Record<string, unknown>;
}

export function getTaskProgress(task: Task) {
    if (!task?.steps?.length) {
        return 0;
    }

    if (task.isComplete || task.isFailed) {
        return 100;
    }

    let [totalWeight, totalProgress] = [0, 0];
    for (const step of task.steps) {
        totalWeight += step.weight;
        totalProgress += step.progress * step.weight;
    }

    if (!totalWeight || !totalProgress) {
        return 0;
    }

    return Math.min(Math.round((totalProgress / totalWeight) * 100), 99);
}

export function getTaskStepStatusMessage(taskStepStatus: TaskStepStatus | undefined) {
    switch (taskStepStatus) {
        case "running":
            return "In progress...";
        case "complete":
            return "Complete";
        default:
            return "Queued";
    }
}

export function getTaskStepName(taskStep: TaskStep | undefined) {
    switch (taskStep?.type) {
        case "transcode":
            return "Transcoding";
        default:
            return "Analysis";
    }
}

export function getTaskStepMessage(taskStep: TaskStep | undefined) {
    return `${getTaskStepName(taskStep)} - ${getTaskStepStatusMessage(taskStep?.status)}`;
}

/** Helper function to determine whether an import task is still processing
 *  a media source for display in the browser. */
export function getVideoProcessingTaskStatus(
    task: Task | undefined,
):
    | { currentStep: TaskStepType | undefined; isProcessing: boolean; progress: number; status: TaskStepStatus }
    | undefined {
    if (task?.type !== "import") {
        return undefined;
    }

    if (task.isComplete || task.isFailed) {
        return { currentStep: undefined, isProcessing: false, progress: 100, status: "complete" };
    }

    const transcodingSteps = task.steps.filter((s) => s.type == "transcode");
    const isTranscoding = transcodingSteps.some((s) => (s.progress ?? 1.0) !== 1.0) ?? false;

    const combiningStep = task.steps.find((s) => s.type == "combine");
    const isCombining = (combiningStep?.progress ?? 1.0) != 1.0;

    const linkingStep = task.steps.find((s) => s.type == "link");
    const isLinking = (linkingStep?.progress ?? 1.0) != 1.0;

    let [totalWeight, totalProgress] = [0, 0];
    let processingStatus: TaskStepStatus = "pending";
    if (transcodingSteps.length) {
        for (const step of transcodingSteps) {
            if (step.status !== "pending") {
                processingStatus = "running";
            }
            totalWeight += step.weight;
            totalProgress += step.progress * step.weight;
        }
    }
    if (combiningStep) {
        if (combiningStep.status !== "pending") {
            processingStatus = "running";
        }
        totalWeight += combiningStep.weight;
        totalProgress += combiningStep.progress * combiningStep.weight;
    }
    if (linkingStep) {
        totalWeight += linkingStep.weight;
        totalProgress += linkingStep.progress * linkingStep.weight;
    }

    let currentStep: TaskStepType;
    if (isTranscoding) {
        currentStep = "transcode";
    } else if (isCombining) {
        currentStep = "combine";
    } else if (isLinking) {
        currentStep == "link";
    }

    return {
        currentStep,
        isProcessing: currentStep !== undefined,
        progress: totalWeight ? Math.min(Math.round((totalProgress / totalWeight) * 100), 99) : 0,
        status: processingStatus,
    };
}
